/** @module @liquidefy/code-editor/circuits/code-editor-full @version 1.0.0 @license LQD Research LLC */
/** @rust code_editor_full.rs — false; 4/2/26; sm */

/**
 * Tier 3 — Full Composition: Batteries-Included Editor
 *
 * Wires ALL major domains into a complete editor surface.
 * One call to get everything: document, editor, themes, diff, AI analysis,
 * terminal theming, workspace, filesystem, and search.
 *
 * Pure circuit. No DOM, no framework coupling.
 */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type { CircuitHandle, CircuitHealthReport, CircuitStats } from './circuit-types.js';
import { buildHealthReport, emptyStats, recordSuccess, recordError, healthFromStats, formatStatsDetails } from './circuit-types.js';

import type {
  DocumentState,
  EditorState,
  EditorResult,
  DiffResult,
  ThemeDefinition,
  WorkspaceState,
  SearchMatch,
  SearchQuery,
  Position,
  ShellType,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asDocumentId,
  asLanguageId,
  asEditorInstanceId,
} from '../code-editor.js';

import { createDocument } from '../document/document.js';
import { createEditor } from '../editor/editor.js';
import { createThemesState, registerTheme, activateTheme } from '../themes/themes.js';
import { createLiquidefyDark, createLiquidefyLight, createLiquidefyMidnight, createLiquidefyHighContrast } from '../themes/identity/presets.js';
import { createDefaultDarkGround, createDefaultLightGround } from '../themes/glass/backdrop.js';
import { computeDiff } from '../diff/compute.js';
import { buildAnalysisRequest, quickAnalysis } from '../ai/agents/analysis.js';
import { createTerminalTheme } from '../terminal/appearance/theme.js';
import { createWorkspace } from '../workspace/workspace.js';
import { createSearchState, startSearch, addResult } from '../search/search.js';
import { createFilesystemState } from '../filesystem/filesystem.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 TYPES
// ═══════════════════════════════════════════════════════════════════════════════

export type ThemePreset = 'dark' | 'light' | 'midnight' | 'high-contrast';

export interface CodeEditorFullConfig {
  readonly content: string;
  readonly uri: string;
  readonly language?: string;
  readonly theme?: ThemePreset;
  readonly enableAI?: boolean;
  readonly enableTerminal?: boolean;
  readonly enableGit?: boolean;
  readonly shell?: ShellType;
}

export interface DocumentStats {
  readonly lines: number;
  readonly words: number;
  readonly characters: number;
  readonly language: string;
  readonly theme: string;
  readonly hasAI: boolean;
  readonly hasTerminal: boolean;
}

export interface AnalysisResult {
  readonly score: number;
  readonly findings: Array<{ line: number; message: string; severity: string }>;
}

export interface CodeEditorFullHandle extends CircuitHandle {
  getDocument(): DocumentState;
  getEditor(): EditorState;
  edit(position: Position, text: string): EditorResult<DocumentState>;
  undo(): EditorResult<DocumentState>;
  redo(): EditorResult<DocumentState>;
  search(query: string): SearchMatch[];
  getTheme(): ThemeDefinition | null;
  switchTheme(preset: ThemePreset): void;
  getCSSVariables(): Record<string, string>;
  diff(otherContent: string): EditorResult<DiffResult>;
  analyzeCode(): AnalysisResult;
  getTerminalTheme(): ReturnType<typeof createTerminalTheme> | null;
  getWorkspace(): WorkspaceState;
  save(): EditorResult<string>;
  getStats(): DocumentStats;
  stats(): CircuitStats;
  health(): CircuitHealthReport;
  destroy(): void;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 PRESET LOADING
// ═══════════════════════════════════════════════════════════════════════════════

function loadPreset(preset: ThemePreset): ThemeDefinition {
  switch (preset) {
    case 'light': return createLiquidefyLight();
    case 'midnight': return createLiquidefyMidnight();
    case 'high-contrast': return createLiquidefyHighContrast();
    case 'dark':
    default: return createLiquidefyDark();
  }
}

function presetToColorScheme(preset: ThemePreset): 'dark' | 'light' {
  return preset === 'light' ? 'light' : 'dark';
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 HISTORY ENGINE — simple undo/redo stack
// ═══════════════════════════════════════════════════════════════════════════════

interface HistoryStack {
  undoStack: string[];
  redoStack: string[];
  maxSize: number;
}

function createHistory(maxSize: number = 200): HistoryStack {
  return { undoStack: [], redoStack: [], maxSize };
}

function pushHistory(history: HistoryStack, content: string): void {
  history.undoStack.push(content);
  if (history.undoStack.length > history.maxSize) {
    history.undoStack.shift();
  }
  history.redoStack.length = 0;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 SEARCH ENGINE — lightweight in-document search
// ═══════════════════════════════════════════════════════════════════════════════

function searchContent(content: string, query: string): SearchMatch[] {
  if (!query) return [];
  const lines = content.split('\n');
  const matches: SearchMatch[] = [];
  const lowerQuery = query.toLowerCase();

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    const lowerLine = line.toLowerCase();
    let col = 0;
    while (true) {
      const idx = lowerLine.indexOf(lowerQuery, col);
      if (idx === -1) break;
      matches.push({
        range: {
          start: { line: i, column: idx },
          end: { line: i, column: idx + query.length },
        },
        preview: line.trim(),
      } as SearchMatch);
      col = idx + 1;
    }
  }
  return matches;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 CODE ANALYSIS — complexity scoring and findings
// ═══════════════════════════════════════════════════════════════════════════════

function analyzeContent(content: string, language: string): AnalysisResult {
  const lines = content.split('\n');
  const findings: Array<{ line: number; message: string; severity: string }> = [];
  let complexityScore = 100;

  const longLineThreshold = 120;
  const highNestingKeywords = /\b(if|for|while|switch|try)\b/g;
  let maxNesting = 0;
  let currentNesting = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;

    // Long line detection
    if (line.length > longLineThreshold) {
      findings.push({ line: i + 1, message: `Line exceeds ${longLineThreshold} characters (${line.length})`, severity: 'info' });
      complexityScore -= 1;
    }

    // Nesting depth tracking
    const opens = (line.match(/\{/g) || []).length;
    const closes = (line.match(/\}/g) || []).length;
    currentNesting += opens - closes;
    if (currentNesting > maxNesting) maxNesting = currentNesting;

    // TODO/FIXME detection
    if (/\b(TODO|FIXME|HACK|XXX)\b/.test(line)) {
      findings.push({ line: i + 1, message: `Contains ${line.match(/\b(TODO|FIXME|HACK|XXX)\b/)![0]} marker`, severity: 'warning' });
    }

    // Console.log detection for JS/TS
    if ((language === 'typescript' || language === 'javascript') && /console\.(log|debug|info)\b/.test(line)) {
      findings.push({ line: i + 1, message: 'Contains console output statement', severity: 'info' });
      complexityScore -= 0.5;
    }
  }

  // Penalize high nesting
  if (maxNesting > 4) {
    complexityScore -= (maxNesting - 4) * 5;
    findings.push({ line: 0, message: `Max nesting depth: ${maxNesting} (recommended < 5)`, severity: 'warning' });
  }

  // Penalize very long files
  if (lines.length > 500) {
    complexityScore -= Math.min(20, Math.floor((lines.length - 500) / 50));
    findings.push({ line: 0, message: `File has ${lines.length} lines (consider splitting)`, severity: 'info' });
  }

  return { score: Math.max(0, Math.min(100, Math.round(complexityScore))), findings };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §7 WORD COUNTER
// ═══════════════════════════════════════════════════════════════════════════════

function countWords(content: string): number {
  const trimmed = content.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §8 FACTORY — createCodeEditorFull
// ═══════════════════════════════════════════════════════════════════════════════

export function createCodeEditorFull(config: CodeEditorFullConfig): CodeEditorFullHandle {
  let circuitStats: CircuitStats = emptyStats();
  let destroyed = false;
  const createdAt = Date.now();

  function guardDestroyed(): void {
    if (destroyed) throw new Error('Circuit destroyed');
  }

  // ── Initialize document ──
  const language = config.language ?? detectLanguage(config.uri);
  const docResult = createDocument(config.content, language);
  if (!docResult.success) throw new Error(`Failed to create document: ${docResult.code}`);
  let documentState: DocumentState = docResult.value;

  // ── Initialize editor ──
  const editorId = asEditorInstanceId(generateId('editor'));
  const editorResult = createEditor(editorId, documentState.id);
  if (!editorResult.success) throw new Error(`Failed to create editor: ${editorResult.code}`);
  let editorState: EditorState = editorResult.value;

  // ── Initialize themes ──
  const themePreset = config.theme ?? 'dark';
  let themesState = createThemesState();

  const allPresets: ThemePreset[] = ['dark', 'light', 'midnight', 'high-contrast'];
  for (const p of allPresets) {
    const def = loadPreset(p);
    const regResult = registerTheme(themesState, def);
    if (regResult.success) themesState = regResult.value;
  }

  const activeTheme = loadPreset(themePreset);
  const activateResult = activateTheme(themesState, activeTheme.id);
  if (activateResult.success) themesState = activateResult.value;

  let currentPreset: ThemePreset = themePreset;

  // ── Initialize workspace ──
  const wsResult = createWorkspace({ name: config.uri, rootFolders: [] });
  if (!wsResult.success) throw new Error(`Failed to create workspace: ${wsResult.code}`);
  let workspaceState: WorkspaceState = wsResult.value;

  // ── History ──
  const history = createHistory();

  // ── Flags ──
  const hasAI = config.enableAI ?? false;
  const hasTerminal = config.enableTerminal ?? false;
  const colorScheme = presetToColorScheme(currentPreset);

  // ── Methods ──

  function getDocument(): DocumentState {
    guardDestroyed();
    circuitStats = recordSuccess(circuitStats);
    return documentState;
  }

  function getEditor(): EditorState {
    guardDestroyed();
    circuitStats = recordSuccess(circuitStats);
    return editorState;
  }

  function edit(position: Position, text: string): EditorResult<DocumentState> {
    guardDestroyed();
    try {
      pushHistory(history, documentState.content);
      const offset = positionToOffset(documentState.content, position);
      const newContent = documentState.content.slice(0, offset) + text + documentState.content.slice(offset);
      documentState = {
        ...documentState,
        content: newContent,
        version: documentState.version + 1,
        lineCount: newContent.split('\n').length,
        charCount: newContent.length,
        isDirty: true,
      };
      circuitStats = recordSuccess(circuitStats);
      return ok(documentState, 'EDIT_APPLIED', 'Edit applied');
    } catch (e) {
      circuitStats = recordError(circuitStats);
      return err('EDIT_FAILED', String(e));
    }
  }

  function undo(): EditorResult<DocumentState> {
    guardDestroyed();
    if (history.undoStack.length === 0) {
      circuitStats = recordError(circuitStats);
      return err('NOTHING_TO_UNDO', 'Undo stack is empty');
    }
    const previous = history.undoStack.pop()!;
    history.redoStack.push(documentState.content);
    documentState = {
      ...documentState,
      content: previous,
      version: documentState.version + 1,
      lineCount: previous.split('\n').length,
      charCount: previous.length,
      isDirty: true,
    };
    circuitStats = recordSuccess(circuitStats);
    return ok(documentState, 'UNDO_APPLIED', 'Undo applied');
  }

  function redo(): EditorResult<DocumentState> {
    guardDestroyed();
    if (history.redoStack.length === 0) {
      circuitStats = recordError(circuitStats);
      return err('NOTHING_TO_REDO', 'Redo stack is empty');
    }
    const next = history.redoStack.pop()!;
    history.undoStack.push(documentState.content);
    documentState = {
      ...documentState,
      content: next,
      version: documentState.version + 1,
      lineCount: next.split('\n').length,
      charCount: next.length,
      isDirty: true,
    };
    circuitStats = recordSuccess(circuitStats);
    return ok(documentState, 'REDO_APPLIED', 'Redo applied');
  }

  function search(query: string): SearchMatch[] {
    guardDestroyed();
    const matches = searchContent(documentState.content, query);
    circuitStats = recordSuccess(circuitStats);
    return matches;
  }

  function getTheme(): ThemeDefinition | null {
    guardDestroyed();
    circuitStats = recordSuccess(circuitStats);
    return themesState.themes.find(t => t.id === themesState.activeThemeId) ?? null;
  }

  function switchTheme(preset: ThemePreset): void {
    guardDestroyed();
    const def = loadPreset(preset);
    const result = activateTheme(themesState, def.id);
    if (result.success) {
      themesState = result.value;
      currentPreset = preset;
    }
    circuitStats = recordSuccess(circuitStats);
  }

  function getCSSVariables(): Record<string, string> {
    guardDestroyed();
    circuitStats = recordSuccess(circuitStats);
    return { ...themesState.cssVariables };
  }

  function diff(otherContent: string): EditorResult<DiffResult> {
    guardDestroyed();
    try {
      const result = computeDiff(documentState.content, otherContent);
      circuitStats = recordSuccess(circuitStats);
      return result;
    } catch (e) {
      circuitStats = recordError(circuitStats);
      return err('DIFF_FAILED', String(e));
    }
  }

  function doAnalyzeCode(): AnalysisResult {
    guardDestroyed();
    const result = analyzeContent(documentState.content, language);
    circuitStats = recordSuccess(circuitStats);
    return result;
  }

  function getTerminalThemeValue(): ReturnType<typeof createTerminalTheme> | null {
    guardDestroyed();
    if (!hasTerminal) return null;
    try {
      const scheme = presetToColorScheme(currentPreset);
      const theme = createTerminalTheme(scheme);
      circuitStats = recordSuccess(circuitStats);
      return theme;
    } catch {
      circuitStats = recordError(circuitStats);
      return null;
    }
  }

  function getWorkspace(): WorkspaceState {
    guardDestroyed();
    circuitStats = recordSuccess(circuitStats);
    return workspaceState;
  }

  function save(): EditorResult<string> {
    guardDestroyed();
    try {
      const snapshot = JSON.stringify({
        uri: config.uri,
        content: documentState.content,
        language,
        version: documentState.version,
        theme: currentPreset,
        savedAt: Date.now(),
      });
      documentState = { ...documentState, isDirty: false };
      circuitStats = recordSuccess(circuitStats);
      return ok(snapshot, 'SAVED', 'Document saved');
    } catch (e) {
      circuitStats = recordError(circuitStats);
      return err('SAVE_FAILED', String(e));
    }
  }

  function getDocStats(): DocumentStats {
    guardDestroyed();
    circuitStats = recordSuccess(circuitStats);
    return {
      lines: documentState.lineCount,
      words: countWords(documentState.content),
      characters: documentState.charCount,
      language,
      theme: currentPreset,
      hasAI,
      hasTerminal,
    };
  }

  function getCircuitStats(): CircuitStats {
    return { ...circuitStats, uptime: Date.now() - createdAt };
  }

  function getHealth(): CircuitHealthReport {
    const h = destroyed ? 'destroyed' as const : healthFromStats(circuitStats);
    return buildHealthReport('code-editor-full', h, formatStatsDetails(circuitStats), createdAt);
  }

  function destroy(): void {
    destroyed = true;
  }

  return Object.freeze({
    name: 'code-editor-full',
    getDocument,
    getEditor,
    edit,
    undo,
    redo,
    search,
    getTheme,
    switchTheme,
    getCSSVariables,
    diff,
    analyzeCode: doAnalyzeCode,
    getTerminalTheme: getTerminalThemeValue,
    getWorkspace,
    save,
    getStats: getDocStats,
    stats: getCircuitStats,
    health: getHealth,
    destroy,
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// §9 HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

function positionToOffset(content: string, position: Position): number {
  const lines = content.split('\n');
  let offset = 0;
  for (let i = 0; i < position.line && i < lines.length; i++) {
    offset += lines[i]!.length + 1;
  }
  return offset + position.column;
}

function detectLanguage(uri: string): string {
  const ext = uri.split('.').pop()?.toLowerCase() ?? '';
  const map: Record<string, string> = {
    ts: 'typescript', tsx: 'typescriptreact', js: 'javascript', jsx: 'javascriptreact',
    py: 'python', rs: 'rust', go: 'go', java: 'java', rb: 'ruby', cpp: 'cpp',
    c: 'c', cs: 'csharp', swift: 'swift', kt: 'kotlin', scala: 'scala',
    html: 'html', css: 'css', scss: 'scss', json: 'json', yaml: 'yaml', yml: 'yaml',
    md: 'markdown', sh: 'shellscript', bash: 'shellscript', zsh: 'shellscript',
    sql: 'sql', xml: 'xml', toml: 'toml', dockerfile: 'dockerfile',
    svelte: 'svelte', vue: 'vue', php: 'php', lua: 'lua', zig: 'zig',
  };
  return map[ext] ?? 'plaintext';
}
